import { ResidentialUnitSelectFilterable } from '@ygtechnologies/bygr-frontend-component-library';
import React, { useMemo, useState } from 'react';
// import theme from './ProjectModulesUnitsModal.module.scss';
import { BygrModule } from '@/types';
import { useAsyncStateWithIsLoading } from '@/utils/hooks';
import { extractErrorMessage } from '@/utils/ajax';
import { showModules } from '@/utils/util';
import { uniq } from 'lodash-es';
import { getResidentialUnitLinkInfo } from '@/services/projects';
import { setModuleForResidentialUnits } from '@/services/residentialUnit';
import { AlertBanner, SlideToggle, Button, ButtonBar, Dialog } from '@ygtechnologies/rubus-ui';

interface ProjectModulesUnitsModalProps {
    isOpen: boolean;
    toggle(): void;
    projectId: number;
}

const ProjectModulesUnitsModal: React.FC<ProjectModulesUnitsModalProps> = ({ isOpen, toggle, projectId }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedUnits, setSelectedUnits] = useState<number[]>([]);
    // Fetched on every open, so the summary below never shows what buyers had before the last save.
    const [units] = useAsyncStateWithIsLoading(
        () => (isOpen ? getResidentialUnitLinkInfo(projectId) : Promise.resolve(null)),
        [projectId, isOpen],
        null
    );
    const [module, setModule] = useState<BygrModule>(BygrModule.None);
    const hasModule = (m: BygrModule) => m === (module & m);

    const unitCountsByBuyerModules = useMemo(() => {
        const counts = new Map<BygrModule, number>();
        units?.forEach((x) => counts.set(x.buyerModules, (counts.get(x.buyerModules) ?? 0) + 1));
        return [...counts.entries()].sort(([a], [b]) => a - b);
    }, [units]);

    const close = () => {
        setError(null);
        toggle();
    };

    const toggleModule = (m: BygrModule) => {
        if (hasModule(m)) {
            setModule(module & ~m);
        } else {
            setModule(module | m);
        }
    };

    const setModuleForUnits = async () => {
        if (module === BygrModule.None) {
            return window.alert('Need to have one module.');
        }
        if (selectedUnits.length === 0) {
            return window.alert('Need to select units.');
        }
        setIsLoading(true);
        setError(null);
        try {
            await setModuleForResidentialUnits(selectedUnits, module);
            close();
        } catch (e) {
            setError((await extractErrorMessage(e)) ?? 'Changing the buyer modules failed.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>Change buyer modules for residential units</Dialog.Header>
                {unitCountsByBuyerModules.length > 0 && (
                    <p>
                        Buyers have today:{' '}
                        {unitCountsByBuyerModules
                            .map(([m, count]) => `${showModules(m) || 'No module'} on ${count} units`)
                            .join(' · ')}
                    </p>
                )}
                <SlideToggle
                    onChange={() => toggleModule(BygrModule.ChangeManagement)}
                    checked={hasModule(BygrModule.ChangeManagement)}>
                    Change management
                </SlideToggle>

                <SlideToggle
                    checked={hasModule(BygrModule.Aftermarket)}
                    onChange={() => toggleModule(BygrModule.Aftermarket)}>
                    Aftermarket
                </SlideToggle>

                {units && (
                    <ResidentialUnitSelectFilterable
                        filterData={{
                            floors: uniq(units.map((x) => x.floor)),
                            groups: uniq(
                                units
                                    .filter((x) => x.residentialUnitGroupId)
                                    .map((x) => ({ name: x.residentialUnitGroupName, id: x.residentialUnitGroupId! }))
                            ),
                            layoutTypes: uniq(units.map((x) => x.layoutType)),
                            tags: [],
                        }}
                        onSelectionChange={(v) => setSelectedUnits(v)}
                        residentialUnitsLinkInfo={units.map((x) => ({ ...x, linkedGroups: [], tags: [] }))}
                        tags={[]}
                        value={selectedUnits}
                    />
                )}
                {error && <AlertBanner type="danger">{error}</AlertBanner>}
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button loading={isLoading} variant="primary" onClick={setModuleForUnits}>
                            Set module
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};

export default ProjectModulesUnitsModal;
